import { Alert, Box, Group, Stack, Text, Title } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'

import { TabKeys } from '@/constants/TabKeys'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { SetupProgress } from '@/services/api/api.types'
import * as classes from '@/styles/SetupChecklist.css'
import { navigateTo } from '@/utils/navigation'

export interface SetupChecklistProps {
  /** Whether to show the checklist - defaults to true */
  visible?: boolean
}

interface SetupStep {
  key: keyof SetupProgress
  label: string
  description?: string
}

const SETUP_STEPS: SetupStep[] = [
  {
    key: 'createContent',
    label: 'Create and Publish Content',
    description: 'Add content modules for your sidekicks',
  },
  {
    key: 'inviteUsers',
    label: 'Invite Users',
    description: 'Invite end users to access your platform',
  },
]

// Map setup steps to navigation targets
const STEP_TO_TAB_MAP: Record<keyof SetupProgress, string> = {
  createContent: TabKeys.createCM,
  inviteUsers: TabKeys.appUsers,
}

export const SetupChecklist = observer(function SetupChecklist(props: SetupChecklistProps) {
  const { visible = true } = props
  const { authenticationStore } = useGlobalState()
  const { setupProgress, isSponsorAdmin, isInSetup } = authenticationStore

  // Only show for sponsor admins and when in setup mode
  if (!visible || !isSponsorAdmin || !isInSetup) {
    return null
  }

  const handleStepClick = (stepKey: keyof SetupProgress) => {
    const tabKey = STEP_TO_TAB_MAP[stepKey]
    if (tabKey) {
      if (stepKey === 'inviteUsers') {
        navigateTo('people', { searchParams: { tab: tabKey } })
      } else if (stepKey === 'createContent') {
        navigateTo('contentModules', { searchParams: { tab: 'all-modules' } })
      }
    }
  }

  return (
    <Box className={classes.container}>
      <Alert
        variant="light"
        color="indigo"
        classNames={{
          root: classes.alert,
          icon: classes.icon,
          body: classes.body,
        }}>
        <Stack gap={0}>
          <div className={classes.header}>
            <Title order={6} className={classes.title}>
              Setup Checklist
            </Title>
          </div>

          <Stack gap={0} className={classes.stepsList}>
            {SETUP_STEPS.map((step, index) => {
              const isCompleted = setupProgress[step.key] || false

              return (
                <Group
                  key={step.key}
                  gap="sm"
                  className={classes.stepItem}
                  onClick={() => handleStepClick(step.key)}
                  data-completed={isCompleted}>
                  <Box className={classes.stepNumber}>
                    {isCompleted ? (
                      <IconCheck size={12} className={classes.stepIcon} />
                    ) : (
                      <Text size="xs" fw={600} className={classes.stepIcon}>
                        {index + 1}
                      </Text>
                    )}
                  </Box>
                  <Text size="sm" className={classes.stepLabel} td={isCompleted ? 'line-through' : undefined}>
                    {step.label}
                  </Text>
                </Group>
              )
            })}
          </Stack>
        </Stack>
      </Alert>
    </Box>
  )
})
